import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useBranch } from '../contexts/BranchContext'
import { useAuth } from '../contexts/AuthContext'
import { washService } from '../services/firebase'
import { roleService } from '../services/roleService'
import {
  ShoppingCart,
  User,
  Car,
  DollarSign,
  Receipt,
  Search,
  Filter,
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { cn } from '../utils/cn'
import { washPaymentBadgeClass, washPaymentLabel, washNeedsCashierPayment } from '../utils/paymentStatus'
import toast from 'react-hot-toast'

export default function POSPage() {
  const { currentBranch, isViewingAllBranches, getEffectiveBranchId } = useBranch()
  const { userProfile } = useAuth()
  const [washes, setWashes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [denied, setDenied] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function run() {
      setDenied(false)

      // Wait for auth profile before deciding permission
      if (!userProfile) {
        setLoading(true)
        return
      }

      if (!roleService.canUserPerformAction(userProfile, 'access_pos')) {
        setDenied(true)
        setWashes([])
        setLoading(false)
        return
      }

      const branchId = getEffectiveBranchId()
      if (!branchId) {
        setLoading(true)
        return
      }

      if (isViewingAllBranches()) {
        setWashes([])
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const allWashes = await washService.getWashesByBranch(branchId)
        if (cancelled) return

        const posReadyWashes = (allWashes || []).filter((wash) =>
          ['waiting', 'washing', 'drying', 'polishing', 'interior', 'ready'].includes(wash.status)
        )

        setWashes(posReadyWashes)
      } catch (error) {
        console.error('Error loading washes:', error)
        if (!cancelled) {
          toast.error('Failed to load wash sessions')
          setWashes([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [currentBranch?.id, currentBranch?.isAllBranches, userProfile, isViewingAllBranches, getEffectiveBranchId])

  function formatTime(timestamp) {
    if (!timestamp) return ''
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleTimeString('en-ZA', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const filteredWashes = washes.filter((wash) => {
    const name = String(wash.customerName || '').toLowerCase()
    const phone = String(wash.customerPhone || '')
    const plate = String(wash.carDetails?.licensePlate || '').toLowerCase()
    const q = searchTerm.toLowerCase()

    const matchesSearch =
      !q || name.includes(q) || phone.includes(searchTerm) || plate.includes(q)

    const matchesStatus = statusFilter === 'all' || wash.status === statusFilter

    return matchesSearch && matchesStatus
  })

  if (loading) {
    return (
      <div className="min-h-96 flex flex-col items-center justify-center gap-3">
        <LoadingSpinner size="large" />
        <p className="text-sm text-muted-foreground">Loading wash payment queue…</p>
      </div>
    )
  }

  if (denied) {
    return (
      <div className="text-center py-12 space-y-3">
        <h2 className="text-xl font-semibold text-gray-900">No POS access</h2>
        <p className="text-gray-600">Your role does not include Access POS.</p>
        <Link to="/pos" className="text-sm font-medium text-primary hover:underline">
          ← Back to POS
        </Link>
      </div>
    )
  }

  if (!currentBranch || isViewingAllBranches()) {
    return (
      <div className="text-center py-12 space-y-3">
        <Receipt className="mx-auto h-16 w-16 text-gray-400 mb-2" />
        <h2 className="text-xl font-semibold text-gray-900">Select a branch</h2>
        <p className="text-gray-600 max-w-md mx-auto">
          Choose a specific branch in the header to see washes waiting for payment.
        </p>
        <Link to="/pos" className="text-sm font-medium text-primary hover:underline">
          ← Back to POS
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Wash payment queue</h1>
        <p className="text-gray-600">
          {currentBranch.name} — select a wash to open POS and take payment
        </p>
        <p className="mt-2">
          <Link to="/pos" className="text-sm font-medium text-primary hover:underline">
            ← Back to POS
          </Link>
        </p>
      </div>

      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by customer name, phone, or license plate..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 w-full"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="all">All Status</option>
              <option value="waiting">Waiting</option>
              <option value="washing">Washing</option>
              <option value="drying">Drying</option>
              <option value="ready">Ready</option>
            </select>
          </div>
        </div>
      </div>

      {filteredWashes.length === 0 ? (
        <div className="text-center py-12">
          <Receipt className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">No wash sessions</h2>
          <p className="text-gray-600 mt-2">
            No open washes for this branch match your filters.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWashes.map((wash) => (
            <WashCard key={wash.id} wash={wash} formatTime={formatTime} />
          ))}
        </div>
      )}
    </div>
  )
}

function WashCard({ wash, formatTime }) {
  const statusColors = {
    waiting: 'bg-yellow-100 text-yellow-800',
    washing: 'bg-blue-100 text-blue-800',
    drying: 'bg-purple-100 text-purple-800',
    polishing: 'bg-indigo-100 text-indigo-800',
    interior: 'bg-cyan-100 text-cyan-800',
    ready: 'bg-green-100 text-green-800',
  }

  const status = String(wash.status || 'waiting')

  return (
    <div className="card p-6 hover:shadow-md transition-shadow">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className={cn('badge text-xs', statusColors[status] || 'bg-gray-100 text-gray-800')}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          <span className="text-xs text-gray-500">{formatTime(wash.createdAt)}</span>
        </div>

        <div className="space-y-2">
          <div className="flex items-center text-sm">
            <User className="h-4 w-4 text-gray-400 mr-2" />
            <span className="font-medium">{wash.customerName || 'Walk-in'}</span>
          </div>
          {wash.customerPhone && (
            <div className="text-xs text-gray-600 ml-6">{wash.customerPhone}</div>
          )}
        </div>

        {wash.carDetails && (
          <div className="space-y-1">
            <div className="flex items-center text-sm">
              <Car className="h-4 w-4 text-gray-400 mr-2" />
              <span>
                {wash.carDetails.make} {wash.carDetails.model}
              </span>
            </div>
            <div className="text-xs text-gray-600 ml-6">
              {wash.carDetails.color}
              {wash.carDetails.licensePlate ? ` • ${wash.carDetails.licensePlate}` : ''}
            </div>
          </div>
        )}

        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-900">{wash.serviceType || 'Wash'}</div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">R{wash.amount}</span>
            <div className="flex items-center">
              <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
              <span className={cn('text-xs', washPaymentBadgeClass(wash))}>
                {washPaymentLabel(wash)}
              </span>
            </div>
          </div>
        </div>

        {!washNeedsCashierPayment(wash) ? (
          <button
            type="button"
            className="btn-primary w-full text-sm py-2 flex items-center justify-center opacity-60 cursor-not-allowed"
            disabled
          >
            {wash.paymentStatus === 'on_account' ? 'On account' : 'Paid'} (POS locked)
          </button>
        ) : (
          <Link
            to={`/pos/${wash.id}`}
            className="btn-primary w-full text-sm py-2 flex items-center justify-center"
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            Open POS
          </Link>
        )}
      </div>
    </div>
  )
}
